import React from 'react';
import { FormatDate, Status, Numbers } from '@weezevent/nacre';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../../context/useAppContext';
import css from '../../styles.module.css';
import { TicketCardHeader, TicketCardContent } from '../../../detail/components/ticket-card/ticketCard';
import SubSection from './SubSection';
import { capitalizeFirstLetter, translateLocale } from '../../../detail/components/utils';
import { ORIGIN_ADD_MANUAL } from '../../../../../models/order';

export default function OrderSummary({ form }) {
    const { t } = useTranslation();
    const locale = i18next.language;
    const [{ event }] = useAppContext();
    const { orderDate, currency, status, statusColor, languageLabel, sessions, inventories, selected_rates } = form;
    const [width, setWidth] = React.useState(window.innerWidth);
    const breakpoint = 1100;

    React.useEffect(() => {
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    const ticketCount = React.useMemo(() => {
        if (!form.use_inventories) {
            let tickets = 0;
            form.selected_rates?.map(rate => {
                tickets += rate.quantity;
            });
            return tickets;
        }
        return form.attendees.length;
    }, [form]);

    const orderStatus = React.useMemo(() => {
        return status === 'valid' ? t('attendees.addAttendees.paymentStatusPaid') : t('attendees.addAttendees.paymentStatusBooked');
    }, [status]);

    const language = React.useMemo(() => {
        if (!languageLabel) {
            return '';
        }
        return capitalizeFirstLetter(translateLocale(locale, languageLabel));
    }, [languageLabel, locale]);

    const baseContent = React.useMemo(() => {
        return (
            <>
                <SubSection name={t('attendees.addAttendees.orderDate')} content={<FormatDate date={orderDate} format="date-numeric" locale={locale} language={locale} />} />
                <SubSection name={t('ticket.common.tickets')} content={ticketCount} />
            </>
        );
    }, [event, form]);

    const subContent = React.useMemo(() => {
        if (event.use_sessions) {
            return <SubSection name={t('ticket.common.seances')} content={sessions?.length} />;
        } else if (event.use_inventories) {
            return <SubSection name={t('ticket.common.inventories')} content={inventories?.length} />;
        }
    }, [event, form, inventories]);

    const totalSession = React.useMemo(() => {
        let totalPrice = 0;
        selected_rates.forEach(rate => {
            totalPrice += rate.price * rate.quantity;
        });
        return totalPrice;
    }, [form]);

    return (
        <>
            <TicketCardHeader className={css['summary-header']}>
                <div className={css['summary-title']}>
                    <h3>{t('attendees.addAttendees.summary.title')}</h3>
                </div>
                <div className={css['summary-header-subsection']}>
                    {baseContent}
                    {subContent}
                </div>
            </TicketCardHeader>
            <TicketCardContent className={css['order-summary-content']}>
                <SubSection
                    name={t('attendees.origin')}
                    content={form.payment_type === ORIGIN_ADD_MANUAL ? t('attendees.filters.ticketOriginAdded') : t('attendees.filters.ticketOriginStock')}
                />
                <div className={css['summary-header-subsection']}>
                    <SubSection
                        name={t('ticket.common.price')}
                        content={<Numbers className={css['summary-price']} value={totalSession} locale={locale} currency={currency} showNullDecimal />}
                    />
                    <SubSection name={t('attendees.addAttendees.summary.payment')} content={<Status color={statusColor} text={width <= breakpoint ? null : orderStatus} />} />

                    <SubSection name={t('ticket.common.language')} content={language} />
                </div>
            </TicketCardContent>
        </>
    );
}
